import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { fetchAllGroups } from '../../../controllers/groupManagementController';
import { searchMembers } from '../../../controllers/memberManagementController';
import {
  fetchBulkCreateMealsStatus,
  previewBulkCreateMeals,
  startBulkCreateMeals,
} from '../../../controllers/meetingMealController';
import { fetchWeeklyMeetingConfig } from '../../../controllers/meetingsController';
import { BULK_MEALS_JOB_STORAGE_KEY } from '../meetingMealsConstants';
import { buildMealFormDefaults, formatMealMoney, formatPeriodLabel, toApiDateTime } from '../meetingMealsFormUtils';
import { formatPaymentOperationError } from '../../../utils/logoPrerequisiteErrors';

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '5px' },
  '& .MuiInputBase-input': { fontSize: '14px' },
};

const TARGET_ALL = 'all';
const TARGET_GROUPS = 'groups';
const TARGET_MEMBERS = 'members';

const BulkCreateMealsDialog = ({ open, onClose, onCompleted }) => {
  const [form, setForm] = useState({ period: '', title: '', dueDate: '', finalDueDate: '', description: '' });
  const [target, setTarget] = useState(TARGET_ALL);
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [memberQuery, setMemberQuery] = useState('');
  const [memberOptions, setMemberOptions] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const pollJob = (id) => {
    stopPolling();
    const tick = async () => {
      try {
        const status = await fetchBulkCreateMealsStatus(id);
        setJobStatus(status);
        if (status.state === 2 || status.state === 3) {
          stopPolling();
          localStorage.removeItem(BULK_MEALS_JOB_STORAGE_KEY);
          setSubmitting(false);
          if (status.state === 2) {
            toast.success(
              `Toplu yemek borcu tamamlandı. Oluşturulan: ${status.createdCount}, Atlanan: ${status.skippedCount}, Hatalı: ${status.failedCount}`,
            );
            if (onCompleted) onCompleted();
          } else {
            toast.error(status.message || 'Toplu işlem başarısız');
          }
        }
      } catch (e) {
        stopPolling();
        setSubmitting(false);
        setError(e?.message || 'Durum alınamadı');
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2500);
  };

  useEffect(() => {
    if (!open) {
      stopPolling();
      return undefined;
    }
    setError('');
    setPreview(null);
    (async () => {
      try {
        const config = await fetchWeeklyMeetingConfig();
        setForm(buildMealFormDefaults(config));
      } catch {
        setForm(buildMealFormDefaults(null));
      }
      try {
        const g = await fetchAllGroups();
        setGroups(Array.isArray(g) ? g : []);
      } catch {
        setGroups([]);
      }
    })();

    const savedJobId = localStorage.getItem(BULK_MEALS_JOB_STORAGE_KEY);
    if (savedJobId) {
      setJobId(savedJobId);
      setSubmitting(true);
      pollJob(savedJobId);
    }

    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || target !== TARGET_MEMBERS) return;
    if (!memberQuery || memberQuery.trim().length < 2) {
      setMemberOptions([]);
      return;
    }
    let active = true;
    setMemberLoading(true);
    searchMembers(memberQuery.trim())
      .then((data) => { if (active) setMemberOptions(Array.isArray(data) ? data : []); })
      .finally(() => { if (active) setMemberLoading(false); });
    return () => { active = false; };
  }, [memberQuery, open, target]);

  const buildTargetPayload = () => ({
    memberIds: target === TARGET_MEMBERS ? selectedMembers.map((m) => m.id) : null,
    groupIds: target === TARGET_GROUPS ? selectedGroups.map((g) => g.id) : null,
  });

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'period' && value) {
        next.title = `${formatPeriodLabel(value)} Haftalık Yemek`;
      }
      return next;
    });
    setPreview(null);
  };

  const validateForm = () => {
    if (!form.period?.trim() || !form.dueDate) {
      setError('Dönem ve vade zorunludur');
      return false;
    }
    if (target === TARGET_GROUPS && selectedGroups.length === 0) {
      setError('En az bir grup seçiniz');
      return false;
    }
    if (target === TARGET_MEMBERS && selectedMembers.length === 0) {
      setError('En az bir üye seçiniz');
      return false;
    }
    return true;
  };

  const handlePreview = async () => {
    if (!validateForm()) return;
    setPreviewLoading(true);
    setError('');
    setPreview(null);
    try {
      const data = await previewBulkCreateMeals({
        period: form.period.trim(),
        ...buildTargetPayload(),
      });
      setPreview(data);
    } catch (e) {
      setError(formatPaymentOperationError(e, { preview: true, actionLabel: 'Toplu yemek önizlemesi' }));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleStart = async () => {
    if (!validateForm()) return;
    if (!preview) {
      setError('Önce önizleme yapınız');
      return;
    }
    if (preview.missingLogoCariCount > 0) {
      setError(`Logo cari kodu eksik ${preview.missingLogoCariCount} üye var. Toplu işlem başlatılamaz.`);
      return;
    }
    if (!preview.targetBillCount || preview.targetBillCount <= 0) {
      setError('Borçlandırılacak devamlı üye bulunamadı');
      return;
    }

    const title = form.title?.trim() || `${formatPeriodLabel(form.period)} Haftalık Yemek`;
    setSubmitting(true);
    setError('');
    setJobStatus(null);
    try {
      const result = await startBulkCreateMeals({
        period: form.period.trim(),
        title,
        dueDate: toApiDateTime(form.dueDate),
        finalDueDate: form.finalDueDate ? toApiDateTime(form.finalDueDate) : null,
        description: form.description?.trim() || null,
        ...buildTargetPayload(),
      });
      const newJobId = result?.jobId;
      if (!newJobId) throw new Error('Job ID alınamadı');
      setJobId(newJobId);
      localStorage.setItem(BULK_MEALS_JOB_STORAGE_KEY, newJobId);
      pollJob(newJobId);
      toast.info('Toplu yemek borçlandırma başlatıldı');
    } catch (e) {
      setSubmitting(false);
      setError(formatPaymentOperationError(e, { bulk: true, actionLabel: 'Toplu yemek borçlandırma' }));
    }
  };

  const progress = jobStatus?.totalMembers
    ? Math.round((jobStatus.processedCount / jobStatus.totalMembers) * 100)
    : 0;

  const showProgress = submitting && jobStatus;

  const previewMembers = Array.isArray(preview?.members) ? preview.members : [];
  const billingMembers = previewMembers.filter((m) => m.willBill);
  const excludedMembers = previewMembers.filter((m) => !m.willBill);

  return (
    <Dialog open={open} onClose={showProgress ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: '18px', fontWeight: 500, display: 'flex', justifyContent: 'space-between', pr: 3 }}>
        Toplu Yemek Borçlandır
        <IconButton onClick={onClose} size="small" disabled={submitting && jobStatus?.state === 1}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{error}</Alert>}
          {!showProgress ? (
            <>
              <Alert severity="info" sx={{ fontSize: '13px' }}>
                Tutar formdan gönderilmez. Backend birim fiyat × o aydaki Salı sayısını hesaplar. Yalnızca devamlı üyeler borçlandırılır.
              </Alert>
              <TextField
                label="Dönem"
                type="month"
                value={form.period}
                onChange={handleChange('period')}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={fieldSx}
              />
              <TextField label="Başlık" value={form.title} onChange={handleChange('title')} fullWidth sx={fieldSx} />
              <TextField
                label="Vade"
                type="date"
                value={form.dueDate}
                onChange={handleChange('dueDate')}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={fieldSx}
              />
              <TextField
                label="Son Ödeme"
                type="date"
                value={form.finalDueDate}
                onChange={handleChange('finalDueDate')}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={fieldSx}
              />
              <TextField
                label="Açıklama"
                value={form.description}
                onChange={handleChange('description')}
                multiline
                rows={2}
                fullWidth
                sx={fieldSx}
              />
              <FormControl>
                <Typography sx={{ fontSize: '13px', fontWeight: 500, mb: 1 }}>Hedef üyeler</Typography>
                <RadioGroup
                  value={target}
                  onChange={(e) => { setTarget(e.target.value); setPreview(null); }}
                >
                  <FormControlLabel value={TARGET_ALL} control={<Radio size="small" />} label="Tüm üyeler (devamlı olanlar)" />
                  <FormControlLabel value={TARGET_GROUPS} control={<Radio size="small" />} label="Grup seç" />
                  <FormControlLabel value={TARGET_MEMBERS} control={<Radio size="small" />} label="Üye seç" />
                </RadioGroup>
              </FormControl>
              {target === TARGET_GROUPS && (
                <Autocomplete
                  multiple
                  options={groups}
                  value={selectedGroups}
                  onChange={(_, v) => { setSelectedGroups(v); setPreview(null); }}
                  getOptionLabel={(g) => g?.name || ''}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  renderInput={(params) => <TextField {...params} label="Gruplar" sx={fieldSx} />}
                />
              )}
              {target === TARGET_MEMBERS && (
                <Autocomplete
                  multiple
                  options={memberOptions}
                  loading={memberLoading}
                  value={selectedMembers}
                  onChange={(_, v) => { setSelectedMembers(v); setPreview(null); }}
                  onInputChange={(_, v) => setMemberQuery(v)}
                  getOptionLabel={(m) => m?.fullName || ''}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  renderInput={(params) => <TextField {...params} label="Üyeler" placeholder="Ara..." sx={fieldSx} />}
                />
              )}

              {preview && (
                <Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                    <Chip size="small" label={`Borçlandırılacak: ${preview.targetBillCount}`} color="primary" />
                    <Chip size="small" label={`Salı sayısı: ${preview.tuesdayCount}`} />
                    <Chip size="small" label={`Birim fiyat: ${formatMealMoney(preview.unitPrice, preview.currency)}`} />
                    <Chip size="small" label={`Kişi başı: ${formatMealMoney(preview.calculatedAmount, preview.currency)}`} />
                    {preview.missingLogoCariCount > 0 && (
                      <Chip size="small" color="error" label={`Logo cari eksik: ${preview.missingLogoCariCount}`} />
                    )}
                  </Stack>
                  {preview.missingLogoCariCount > 0 && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      Logo cari kodu eksik üyeler var. Toplu işlem başlatılamaz.
                    </Alert>
                  )}
                  <TableContainer sx={{ maxHeight: 240 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Üye</TableCell>
                          <TableCell>Üyelik</TableCell>
                          <TableCell>Borçlandır</TableCell>
                          <TableCell>Logo Cari</TableCell>
                          <TableCell>Açıklama</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {billingMembers.map((m) => (
                          <TableRow key={m.memberId}>
                            <TableCell>{m.memberName}</TableCell>
                            <TableCell>{m.membershipTypeName}</TableCell>
                            <TableCell>Evet</TableCell>
                            <TableCell>{m.hasLogoCariCode ? 'Var' : 'Yok'}</TableCell>
                            <TableCell>{m.reason || '-'}</TableCell>
                          </TableRow>
                        ))}
                        {excludedMembers.slice(0, 10).map((m) => (
                          <TableRow key={`ex-${m.memberId}`} sx={{ opacity: 0.7 }}>
                            <TableCell>{m.memberName}</TableCell>
                            <TableCell>{m.membershipTypeName}</TableCell>
                            <TableCell>Hayır</TableCell>
                            <TableCell>{m.hasLogoCariCode ? 'Var' : 'Yok'}</TableCell>
                            <TableCell>{m.reason || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {excludedMembers.length > 10 && (
                    <Typography sx={{ fontSize: '12px', color: '#a3a3a3', mt: 0.5 }}>
                      +{excludedMembers.length - 10} hariç tutulan üye daha
                    </Typography>
                  )}
                </Box>
              )}
            </>
          ) : (
            <Box>
              <Typography sx={{ fontSize: '14px', mb: 1 }}>
                {jobStatus.stateName || 'İşleniyor'} — {jobStatus.message || ''}
              </Typography>
              {jobStatus.totalMembers > 0 && (
                <>
                  <LinearProgress variant="determinate" value={progress} sx={{ mb: 1 }} />
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip size="small" label={`İşlenen: ${jobStatus.processedCount}/${jobStatus.totalMembers}`} />
                    <Chip size="small" color="success" label={`Oluşturulan: ${jobStatus.createdCount}`} />
                    <Chip size="small" label={`Atlanan: ${jobStatus.skippedCount}`} />
                    <Chip size="small" color="error" label={`Hatalı: ${jobStatus.failedCount}`} />
                  </Stack>
                </>
              )}
              {Array.isArray(jobStatus.items) && jobStatus.items.length > 0 && jobStatus.state === 2 && (
                <TableContainer sx={{ mt: 2, maxHeight: 280 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Üye</TableCell>
                        <TableCell>Sonuç</TableCell>
                        <TableCell>Açıklama</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {jobStatus.items.map((item, idx) => (
                        <TableRow key={`${item.memberId}-${idx}`}>
                          <TableCell>{item.memberName}</TableCell>
                          <TableCell>{item.outcome}</TableCell>
                          <TableCell>{item.reason || item.logoSlipNo || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting && jobStatus?.state === 1} sx={{ textTransform: 'none' }}>
          {showProgress && jobStatus?.state === 2 ? 'Kapat' : 'İptal'}
        </Button>
        {!showProgress && (
          <>
            <Button
              variant="outlined"
              onClick={handlePreview}
              disabled={previewLoading || submitting}
              sx={{ textTransform: 'none' }}
            >
              {previewLoading ? <CircularProgress size={18} /> : 'Önizle'}
            </Button>
            <Button
              variant="contained"
              onClick={handleStart}
              disabled={submitting || !preview || previewLoading || preview?.missingLogoCariCount > 0}
              sx={{ textTransform: 'none', backgroundColor: '#0E4D94' }}
            >
              {submitting ? <CircularProgress size={18} color="inherit" /> : 'Başlat'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkCreateMealsDialog;
